import { zodResolver } from '@hookform/resolvers/zod';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import { createInvitation, fetchInvitations, fetchRoles, revokeInvitation } from '@/api/rbac';
import { PermissionGate } from '@/components/PermissionGate';
import type { Invitation } from '@/types/api';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  role_id: z.string().min(1, 'Choose a role'),
});

type FormValues = z.infer<typeof schema>;

export function InvitationsPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const invitations = useQuery({ queryKey: ['invitations'], queryFn: fetchInvitations });
  const roles = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });

  const {
    register,
    handleSubmit,
    reset,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', role_id: '' },
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['invitations'] });
  };

  const revoke = useMutation({ mutationFn: revokeInvitation, onSuccess: invalidate });

  const onSubmit = async (values: FormValues) => {
    setError(null);
    setSent(null);

    try {
      await createInvitation(values.email, values.role_id);
      setSent(values.email);
      reset({ email: '', role_id: values.role_id });
      await invalidate();
    } catch (e) {
      if (e instanceof ApiError && e.fieldErrors.email) {
        setFieldError('email', {
          message: e.fieldErrors.email[0] ?? 'Invalid email',
        });
        return;
      }
      setError(e instanceof ApiError ? (e.detail ?? e.title) : 'Could not send the invitation.');
    }
  };

  if (invitations.isPending || roles.isPending) return <CircularProgress size={24} />;
  if (invitations.error)
    return <Alert severity="error">You do not have access to invitations.</Alert>;

  return (
    <Stack spacing={3}>
      <Typography variant="h1">Invitations</Typography>

      <PermissionGate permission="invitation:create">
        <Card>
          <CardContent>
            <Typography variant="h3" sx={{ mb: 2 }}>
              Invite someone
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {sent && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSent(null)}>
                An invitation is on its way to {sent}. The link expires in 7 days.
              </Alert>
            )}

            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  {...register('email')}
                />
                <TextField
                  select
                  label="Role"
                  sx={{ minWidth: 200 }}
                  defaultValue=""
                  error={Boolean(errors.role_id)}
                  helperText={errors.role_id?.message}
                  {...register('role_id')}
                >
                  {roles.data?.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Send invite'}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </PermissionGate>

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.data?.data.map((invitation: Invitation) => (
              <TableRow key={invitation.id} hover>
                <TableCell>{invitation.email}</TableCell>
                <TableCell>{invitation.role_label}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      invitation.status === 'accepted'
                        ? 'success'
                        : invitation.status === 'expired'
                          ? 'error'
                          : 'warning'
                    }
                    label={invitation.status}
                  />
                </TableCell>
                <TableCell>{new Date(invitation.expires_at).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <PermissionGate permission="invitation:revoke">
                    {invitation.status === 'pending' && (
                      <IconButton size="small" onClick={() => revoke.mutate(invitation.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </PermissionGate>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Stack>
  );
}
